import { registerSummaryGridRefresher, TITLE_WIDTH, TOTAL_WIDTH } from './schedule-five-summary-shared';
import { getStartDateForData } from './schedule-five-individual-combined-elements';
import { buildTopLevelRows, buildChildRows, buildLaborSubRows } from './schedule-five-cost-code-helpers';

let _k_costCodeGrid = null;
let _rawCostCodeData = [];

// Account for hierarchy cell in expandable grids
const EXPANDABLE_TITLE_WIDTH = TITLE_WIDTH - 32;
const NESTED_TITLE_WIDTH = EXPANDABLE_TITLE_WIDTH - 32;

// Kendo template for a total column that holds mixed currency / hours / fte rows.
const TOTAL_MIXED_TEMPLATE =
    `#if(format==="currency"){#` +
    `<span style="float:left" class="#=total<0?'t-danger':'t-normal'#">$</span>` +
    `<span style="float:right" class="#=total<0?'t-danger':'t-normal'#">#=kendo.toString(Number(total),'n2')#</span>` +
    `#}else if(format==="hours"){#` +
    `<span style="float:right">#=kendo.toString(Math.round(Number(total)),'n0')# h</span>` +
    `#}else if(format==="fte"){#` +
    `<span style="float:right">#=kendo.toString(Number(total),'n2')# FTE</span>` +
    `#}#`;

// Kendo template for a total column that holds only hours / fte rows.
const TOTAL_HOURS_FTE_TEMPLATE =
    `#if(format==="fte"){#` +
    `<span style="float:right">#=kendo.toString(Number(total),'n2')# FTE</span>` +
    `#}else{#` +
    `<span style="float:right">#=kendo.toString(Math.round(Number(total)),'n0')# h</span>` +
    `#}#`;

const PROJECT_NUMBER_NAME_TEMPLATE =
    `# if (projectNumber) { #` +
    `<span class="js-project-tooltip" data-title="#:projectNumber# - #:projectName#">#:projectNumber# - #:projectName#</span>` +
    `# } #`;

// After grid data bind: hide expand arrows on FTE companion rows and apply styling.
const hideSubRowExpanders = (me) => {
    me.grid.dataSource.data().forEach(item => {
        if (item.isSubRow) {
            const $row = me.grid.tbody.find(`tr[data-uid="${item.uid}"]`);
            $row.addClass('sub-row');
            $row.find('.k-hierarchy-cell a').hide();
        }
    });
};

const applyProjectChildFrozenTransform = ($projectGrid, tx) => {
    $projectGrid
        .find('> .k-grid-content tbody tr td:nth-child(1), > .k-grid-content tbody tr td:nth-child(2)')
        .css('transform', tx);
    $projectGrid
        .find('> .k-grid-header .k-grid-header-wrap thead tr th:nth-child(1), > .k-grid-header .k-grid-header-wrap thead tr th:nth-child(2)')
        .css('transform', tx);
};

const applyLaborSubFrozenTransform = ($laborGrid, tx) => {
    $laborGrid
        .find('> .k-grid-content table[role="treegrid"] > tbody > tr > td:not(.k-detail-cell):nth-child(1), > .k-grid-content table[role="treegrid"] > tbody > tr > td:not(.k-detail-cell):nth-child(2), > .k-grid-content table[role="treegrid"] > tbody > tr > td:not(.k-detail-cell):nth-child(3)')
        .css('transform', tx);
    $laborGrid
        .find('> .k-grid-header .k-grid-header-wrap thead tr th:nth-child(1), > .k-grid-header .k-grid-header-wrap thead tr th:nth-child(2), > .k-grid-header .k-grid-header-wrap thead tr th:nth-child(3)')
        .css('transform', tx);
};

const getTopLevelScrollLeft = ($el) => {
    const $topGridContent = $el.closest('.s5-cost-code-wrap').find('.grid.cost-code-grid > [data-role="grid"] > .k-grid-content').first();
    return $topGridContent.scrollLeft() || 0;
};

// Replace the native horizontal scrollbar with a proxy div starting after the frozen columns.
// The native scrollbar is hidden via CSS, but Kendo can set overflow:auto inline at grid init;
// force overflow-x:hidden via JS to ensure the inline style is also overridden.
const makeAttachProxyScrollbar = (frozenWidth) => (me) => {
    const $grid = $(me.grid.element);
    $grid.find('.k-proxy-hscroll').remove();
    const $content = $grid.find('> .k-grid-content');
    const $headerWrap = $grid.find('> .k-grid-header > .k-grid-header-wrap');
    $content.off('scroll.proxyHScroll');
    const contentEl = $content.get(0);
    if (contentEl) {
        // Kendo may write inline overflow:auto after init; use !important to keep native x-scroll hidden.
        contentEl.style.setProperty('overflow-x', 'hidden', 'important');
    }
    const $proxy = $('<div class="k-proxy-hscroll"><div></div></div>');
    $proxy.css('left', frozenWidth);
    $grid.append($proxy);
    const $proxyInner = $proxy.find('div');
    let maxScrollable = 0;

    const updateProxyMetrics = () => {
        const headerNode = $headerWrap.get(0);
        const contentNode = $content.get(0);
        const proxyNode = $proxy.get(0);
        if (!headerNode || !contentNode || !proxyNode) return;

        const headerMax = Math.max(0, headerNode.scrollWidth - headerNode.clientWidth);
        const contentMax = Math.max(0, contentNode.scrollWidth - contentNode.clientWidth);

        // Pick the best max scroll:
        // - headerMax avoids nested-detail inflation in most cases
        // - contentMax can be more accurate when header reports too small
        // If content is drastically larger, treat it as inflated and trust header.
        maxScrollable = contentMax > headerMax * 1.5 ? headerMax : Math.max(headerMax, contentMax);

        const proxyClientWidth = proxyNode.clientWidth;
        $proxyInner.css('width', proxyClientWidth + maxScrollable);
        $proxy.scrollLeft($content.scrollLeft());
    };
    const syncChildEntries = (scrollPos) => {
        const tx = scrollPos === 0 ? '' : `translateX(${scrollPos}px)`;
        // Labor sub entry grids: compensate parent scroll on frozen columns only.
        $grid.find('.k-labor-sub-entry').each(function () {
            applyLaborSubFrozenTransform($(this), tx);
        });

        // Project child entries (including nested under labor sub)
        // compensate against shared top-level scroll.
        $grid.find('.k-project-child-entry').each(function () {
            applyProjectChildFrozenTransform($(this), tx);
        });
    };

    let syncing = false;
    $proxy.on('scroll', () => {
        if (syncing) return; syncing = true;
        const target = Math.min($proxy.scrollLeft(), maxScrollable);
        $content.scrollLeft(target);
        const actual = $content.scrollLeft();
        if (actual !== target) {
            $proxy.scrollLeft(actual);
        }
        syncChildEntries(actual);
        syncing = false;
    });
    $content.on('scroll.proxyHScroll', () => {
        if (syncing) return; syncing = true;
        const actual = Math.min($content.scrollLeft(), maxScrollable);
        $proxy.scrollLeft(actual);
        syncChildEntries(actual);
        syncing = false;
    });

    // Run once now and once on next tick for post-layout widths.
    updateProxyMetrics();
    setTimeout(updateProxyMetrics, 0);
};

// Parent grids (hierarchy 32 + name 150 + total 120) = TITLE_WIDTH + TOTAL_WIDTH = 302px.
const attachProxyScrollbar = makeAttachProxyScrollbar(TITLE_WIDTH + TOTAL_WIDTH);

// Project child entry grids: frozen cols are kept visible by applying translateX(scrollPos)
// to each frozen cell, compensating for the parent scroll so they stay at x=32/182 in
// the parent viewport. Apply initial transform if the parent is already scrolled.
const markProjectChildEntry = (me) => {
    const $childGrid = $(me.grid.element);
    $childGrid.addClass('k-project-child-entry');
    const gridEl = $childGrid.get(0);
    if (gridEl) {
        gridEl.style.setProperty('max-height', 'none', 'important');
    }
    const childContentEl = $childGrid.find('> .k-grid-content').get(0);
    if (childContentEl) {
        childContentEl.style.setProperty('max-height', 'none', 'important');
        childContentEl.style.setProperty('overflow-y', 'visible', 'important');
    }
    const scrollPos = getTopLevelScrollLeft($childGrid);
    if (scrollPos > 0) {
        const tx = `translateX(${scrollPos}px)`;
        applyProjectChildFrozenTransform($childGrid, tx);
    }

    // Initialize tooltip
    $childGrid.kendoTooltip({
        filter: '.js-project-tooltip',
        position: 'top',
        content: function(e) {
            return e.target.data('title');
        }
    });
};

// Labor sub entry grids: no local proxy scrollbar. They are driven by the top-level proxy.
const makeLaborSubDataBound = (hideExpanders) => (me) => {
    const $childGrid = $(me.grid.element);
    $childGrid.addClass('k-labor-sub-entry');
    const gridEl = $childGrid.get(0);
    if (gridEl) {
        gridEl.style.setProperty('max-height', 'none', 'important');
    }
    const $content = $childGrid.find('> .k-grid-content');
    $childGrid.find('.k-proxy-hscroll').remove();
    $content.off('scroll.proxyHScroll');
    const contentEl = $content.get(0);
    if (contentEl) {
        contentEl.style.setProperty('max-height', 'none', 'important');
        contentEl.style.setProperty('overflow-y', 'visible', 'important');
        // Ensure labor sub keeps native x-scroll hidden even if Kendo rewrites inline overflow.
        contentEl.style.setProperty('overflow-x', 'hidden', 'important');
    }
    const scrollPos = getTopLevelScrollLeft($childGrid);
    if (scrollPos > 0) {
        const tx = `translateX(${scrollPos}px)`;
        applyLaborSubFrozenTransform($childGrid, tx);
    }
    if (hideExpanders) hideSubRowExpanders(me);
};

const onDataBound = (me) => {
    hideSubRowExpanders(me);
    attachProxyScrollbar(me);
};
// ─── child grid builders ──────────────────────────────────────────────────────

const buildProjectChildEntry = ($container, rows, costCode, startDate, isNestedUnderLabor = false) => {
    const childRows = buildChildRows(rows, costCode);
    const isHours = costCode >= 3 && costCode <= 6;
    const projectTitleWidth = isNestedUnderLabor ? NESTED_TITLE_WIDTH : EXPANDABLE_TITLE_WIDTH;
    $container.kendoResourceEntry({
        columnWidth: 120,
        dataSource: { schema: { model: {} }, data: childRows },
        startDate,
        input: isHours ? { type: 'columnBased', typeColumn: 'format' } : { type: 'currency' },
        noLockedColumns: true,
        staticColumns: [
            {
                field: 'projectNumber',
                title: 'Project',
                width: projectTitleWidth,
                editable: false,
                template: PROJECT_NUMBER_NAME_TEMPLATE,
            },
            {
                field: 'total',
                title: 'Total',
                width: TOTAL_WIDTH,
                template: isHours ? TOTAL_HOURS_FTE_TEMPLATE : undefined,
                type: isHours ? undefined : 'currency',
                editable: false,
            },
        ],
        collapsible: false,
        readonly: true,
        dataBound: markProjectChildEntry,
        grid: {
            sortable: false,
            noRecords: {
                template: 'There are no projections for this cost type',
            },
        },
    });
};

const buildLaborSubEntry = ($container, rows, isLaborCost, startDate) => {
    const subRows = buildLaborSubRows(rows, isLaborCost);

    $container.kendoResourceEntry({
        columnWidth: 120,
        dataSource: { schema: { model: {} }, data: subRows },
        startDate,
        input: isLaborCost ? { type: 'currency' } : { type: 'columnBased', typeColumn: 'format' },
        staticColumns: [
            { field: 'name', title: ' ', width: NESTED_TITLE_WIDTH, editable: false },
            {
                field: 'total',
                title: 'Total',
                width: TOTAL_WIDTH,
                template: isLaborCost ? undefined : TOTAL_HOURS_FTE_TEMPLATE,
                type: isLaborCost ? 'currency' : undefined,
                editable: false,
            },
        ],
        collapsible: false,
        readonly: true,
        dataBound: makeLaborSubDataBound(!isLaborCost),
        grid: {
            sortable: false,
            detailInit: e => {
                if (e.data.isSubRow) return;
                const $inner = $('<div/>').appendTo(e.detailCell);
                buildProjectChildEntry($inner, rows, e.data.costCode, startDate, true);
            },
        },
    });
};

// ─── top-level detail expand ──────────────────────────────────────────────────

const onDetailInit = (rows, startDate) => e => {
    if (e.data.isSubRow) return;
    const section = e.data.section;
    const $container = $('<div/>').appendTo(e.detailCell);

    if (section === 'laborHours') {
        buildLaborSubEntry($container, rows, false, startDate);
        return;
    }

    if (section === 'laborCosts') {
        buildLaborSubEntry($container, rows, true, startDate);
        return;
    }

    const costCodeMap = {
        equipment: 1,
        material: 2,
        subcontractors: 7,
        otherCosts: 8,
        generalConditions: 9,
    };
    const code = costCodeMap[section];
    if (code == null) return;

    buildProjectChildEntry($container, rows, code, startDate, false);
};

// ─── top-level grid ───────────────────────────────────────────────────────────

const createCostCodeGridInternal = rows => {
    _rawCostCodeData = rows;
    const $grid = $('.grid.cost-code-grid');
    _k_costCodeGrid?.grid?.destroy();
    $grid.empty();

    const topLevelRows = buildTopLevelRows(rows);
    let startDate = getStartDateForData(topLevelRows);
    if (isNaN(startDate.getTime())) startDate = new Date();

    _k_costCodeGrid = $grid
        .kendoResourceEntry({
            columnWidth: 120,
            dataSource: { schema: { model: {} }, data: topLevelRows },
            startDate,
            input: { type: 'columnBased', typeColumn: 'format' },
            staticColumns: [
                { field: 'name', title: ' ', width: EXPANDABLE_TITLE_WIDTH, editable: false },
                { field: 'total', title: 'Total', width: TOTAL_WIDTH, template: TOTAL_MIXED_TEMPLATE, editable: false },
            ],
            collapsible: false,
            readonly: true,
            dataBound: onDataBound,
            grid: {
                sortable: false,
                detailInit: onDetailInit(rows, startDate),
            },
        })
        .data('kendoResourceEntry');
};

export const createCostCodeGrid = (filters = {}) => {
    const company = $('.grid.cost-code-grid').data('company');
    return $.getJSON('/Operations/ScheduleFiveOverview/GetCostCodeSummary', { company, ...filters }).done(data =>
        createCostCodeGridInternal(data)
    );
};

export const initCostCodeGrid = () => {
    registerSummaryGridRefresher(createCostCodeGrid);
    createCostCodeGridInternal([]);
    kendo.ui.progress($('.s5-cost-code-wrap'), true);

    return Promise.all([createCostCodeGrid()])
        .then(() => kendo.ui.progress($('.s5-cost-code-wrap'), false))
        .catch(() => kendo.ui.progress($('.s5-cost-code-wrap'), false));
};

export const getCostCodeRawData = () => _rawCostCodeData;
